import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom'
import { AuthProvider } from '@/context/auth-context'
import { PrivateRoute } from '@/components/auth/private-route'
import Header from './components/layout/header'
import Footer from './components/layout/footer'
import HomePage from './pages/home-page'
import AboutPage from './pages/about-page'
import CoursesPage from './pages/courses-page'
import MentorshipPage from './pages/mentorship-page'
import TestEnginePage from './pages/test-engine-page'
import TestInstructionsPage from './pages/test-instructions-page'
import TestExamPage from './pages/test-exam-page'
import TestResultsPage from './pages/test-results-page'
import PartnerInstitutesPage from './pages/partner-institutes-page'
import ForeignEducationPage from './pages/foreign-education-page'
import MarketplacePage from './pages/marketplace-page'
import CheckoutPage from './pages/checkout-page'
import StudentDashboardPage from './pages/student/dashboard-page'
import StudentTestInstructionsPage from './pages/student/student-test-instructions-page'
import StudentTestExamPage from './pages/student/student-test-exam-page'
import StudentTestResultsPage from './pages/student/student-test-results-page'
import LoginPage from './pages/auth/login-page'
import SignupPage from './pages/auth/signup-page'
import ContactPage from './pages/contact-page'
import NewsPage from './pages/news-page'
import BlogsPage from './pages/blogs-page'
import NewsDetailPage from './pages/news-detail-page'
import BlogDetailPage from './pages/blog-detail-page'
import { MentorDashboardPage } from './pages/mentor/mentor-dashboard-page'
import { AdminDashboardPage } from './pages/admin/admin-dashboard-page'
import { SubAdminDashboardPage } from './pages/admin/sub-admin-dashboard-page'
import { CounsellorDashboardPage } from './pages/counsellor/counsellor-dashboard-page'
import { CounsellorProfilePage } from './pages/counsellor/counsellor-profile-page'

import { AnimatedBackground } from '@/components/ui/animated-background'
import { ScrollToTop } from '@/components/ui/scroll-to-top'

function AppContent() {
    const location = useLocation()

    // Hide header and footer for specific routes (Test Exam, Dashboards, Student Portal)
    const hideLayoutRoutes = [
        '/test/exam/',
        '/test/instructions/',
        '/dashboard',
        '/mentor/dashboard',
        '/admin/dashboard',
        '/sub-admin/dashboard',
        '/counsellor/dashboard',
        '/student/test/',
        '/student/dashboard'
    ]
    const shouldHideLayout = hideLayoutRoutes.some(route => location.pathname.includes(route))

    return (
        <div className="min-h-screen relative">
            <AnimatedBackground />
            {!shouldHideLayout && <Header />}
            <main>
                <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/courses" element={<CoursesPage />} />
                    <Route path="/mentorship" element={<MentorshipPage />} />
                    <Route path="/test-engine" element={<TestEnginePage />} />
                    <Route path="/test/instructions/:testId" element={<TestInstructionsPage />} />
                    <Route path="/test/exam/:testId" element={<TestExamPage />} />
                    <Route path="/test/results" element={<TestResultsPage />} />
                    <Route path="/partner-institutes" element={<PartnerInstitutesPage />} />
                    <Route path="/foreign-education" element={<ForeignEducationPage />} />
                    <Route path="/marketplace" element={<MarketplacePage />} />
                    <Route path="/checkout" element={<CheckoutPage />} />
                    {/* Student Portal Routes - Protected */}
                    <Route
                        path="/dashboard"
                        element={
                            <PrivateRoute allowedRoles={['student']}>
                                <StudentDashboardPage />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/student/dashboard"
                        element={
                            <PrivateRoute allowedRoles={['student']}>
                                <StudentDashboardPage />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/student/test/instructions/:testId"
                        element={
                            <PrivateRoute allowedRoles={['student']}>
                                <StudentTestInstructionsPage />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/student/test/exam/:testId"
                        element={
                            <PrivateRoute allowedRoles={['student']}>
                                <StudentTestExamPage />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/student/test/results"
                        element={
                            <PrivateRoute allowedRoles={['student']}>
                                <StudentTestResultsPage />
                            </PrivateRoute>
                        }
                    />
                    {/* Mentor Portal Routes - Protected */}
                    <Route
                        path="/mentor/dashboard"
                        element={
                            <PrivateRoute allowedRoles={['mentor']}>
                                <MentorDashboardPage />
                            </PrivateRoute>
                        }
                    />
                    {/* Admin Dashboard - Protected */}
                    <Route
                        path="/admin/dashboard"
                        element={
                            <PrivateRoute allowedRoles={['admin']}>
                                <AdminDashboardPage />
                            </PrivateRoute>
                        }
                    />
                    {/* Sub Admin Dashboard - Protected */}
                    <Route
                        path="/sub-admin/dashboard"
                        element={
                            <PrivateRoute allowedRoles={['sub-admin']}>
                                <SubAdminDashboardPage />
                            </PrivateRoute>
                        }
                    />
                    {/* Counsellor Dashboard - Protected */}
                    <Route
                        path="/counsellor/dashboard"
                        element={
                            <PrivateRoute allowedRoles={['counsellor']}>
                                <CounsellorDashboardPage />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/counsellor/profile"
                        element={
                            <PrivateRoute allowedRoles={['counsellor']}>
                                <CounsellorProfilePage />
                            </PrivateRoute>
                        }
                    />
                    {/* <Route path="/pricing" element={<PricingPage />} /> */}
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/news" element={<NewsPage />} />
                    <Route path="/news/:id" element={<NewsDetailPage />} />
                    <Route path="/blogs" element={<BlogsPage />} />
                    <Route path="/blogs/:id" element={<BlogDetailPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/signup" element={<SignupPage />} />
                </Routes>
            </main>
            {!shouldHideLayout && <Footer />}
        </div>
    )
}

function App() {
    return (
        <AuthProvider>
            <Router>
                <ScrollToTop />
                <AppContent />
            </Router>
        </AuthProvider>
    )
}

export default App